import { useMemo } from 'react'
import * as Prismic from '@prismicio/client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import {
  dhlExtraPromotionOneOffPredicate,
  type PrismicPromotionDto,
  unwrapPrismicPromotion,
  dhlExtraPromotionOneOffType,
  type PrismicPromotionParams,
  type PrismicError,
  type PrismicPromotion,
} from '../model/prismic.js'

const fetchByPrismicQuery = async (
  endpoint: string,
  query: undefined | string | string[],
  options: Prismic.QueryParams = {}
): Promise<PrismicPromotion[]> => {
  const api = Prismic.createClient(endpoint, {})
  const response = await api.get<PrismicPromotionDto>({
    filters: query,
    ...options,
  })

  return !response?.results ? [] : response.results.map(unwrapPrismicPromotion)
}

export const useGetPrismicPromotions = (
  { prismicEndpoint, lang }: PrismicPromotionParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof fetchByPrismicQuery>>, PrismicError, PrismicPromotion[]>
  }
): ReturnType<typeof useQuery<PrismicPromotion[], PrismicError, PrismicPromotion[]>> => {
  const query = useMemo(() => [dhlExtraPromotionOneOffPredicate], [])

  const queryOptions: Prismic.QueryParams = useMemo(
    () => ({
      orderings: [{ field: 'document.last_publication_date', direction: 'desc' }],
      lang: lang?.toLowerCase().replace('_', '-'),
    }),
    [lang]
  )

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- options are not relevant for query key
  return useQuery({
    queryKey: [`${prismicEndpoint}/${dhlExtraPromotionOneOffType}/${lang}`],
    queryFn: () => fetchByPrismicQuery(prismicEndpoint, query, queryOptions),
    ...(options?.query ?? {}),
  })
}
